import { makeStyles } from '@material-ui/styles'
import DocumentMeta from 'core/components/DocumentMeta'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import usePluginRouter from 'core/hooks/usePluginRouter'
import useUpdateAction from 'core/hooks/useUpdateAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import GroupMultiSelect from 'k8s/components/common/GroupMultiSelect'
import NamespacePicklist from 'k8s/components/common/NamespacePicklist'
import UserMultiSelect from 'k8s/components/common/UserMultiSelect'
import { K8sPluginGlobalPrefs } from 'k8s/model'
import React from 'react'
import useReactRouter from 'use-react-router'
import RolePicklist from '../RolePicklist'
import { createRoleBinding } from './actions'

const useStyles = makeStyles<Theme>((theme) => ({
  validatedFormContainer: {
    display: 'grid',
    gridGap: 16,
  },
}))

const defaultParams = {
  namespace: '',
  role: null,
  users: [],
  groups: [],
}

export default function AddRoleBindingForm() {
  const classes = useStyles({})
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const { history } = useReactRouter()
  const { currentPluginId } = usePluginRouter()
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences<K8sPluginGlobalPrefs>(
    'k8sPluginGlobalParams',
  )
  const clusterId = k8sPluginGlobalPerfs?.clusterId || ''
  const clusterName = k8sPluginGlobalPerfs?.cluster || ''

  const { update, updating, error: errorCreatingRoleBinding, reset } = useUpdateAction(
    createRoleBinding,
  )

  const submitForm = async (params) => {
    const { success } = await update(params)
    if (success) handleClose()
  }

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    history.push(
      routeManager.routes.kubernetes.accessControl.roleBindings.path({
        cluster: clusterName,
        plugin: currentPluginId,
      }),
    )
  }

  return (
    <>
      <DocumentMeta
        title="Add Role Binding"
        bodyClasses={['form-view']}
        data-testid="document-meta-add-role-binding"
      />
      <ModalForm
        route={routeManager.routes.kubernetes.accessControl.addRoleBindings}
        title="Add Role Binding"
        onSubmit={submitForm}
        onClose={handleClose}
        submitting={updating}
        error={errorCreatingRoleBinding}
        submitTitle="Add Role Binding"
        data-testid="modal-form-add-role-binding"
      >
        <>
          <FormFieldSection
            title="Basic Details"
            step={1}
            data-testid="basic-details-form-field-section"
          >
            <TextField id="name" label="Name" required data-testid="name-text-field" />
            <PicklistField
              DropdownComponent={NamespacePicklist}
              id="namespace"
              label="Namespace"
              clusterId={clusterId}
              onChange={getParamsUpdater('namespace')}
              value={params.namespace}
              setInitialNamespace
              compact={false}
              showAll={false}
              required
              data-testid="namespace-picklist-field"
            />
            <PicklistField
              DropdownComponent={RolePicklist}
              id="role"
              label="Role"
              clusterId={clusterId}
              onChange={getParamsUpdater('role')}
              value={params.role}
              namespace={params.namespace}
              showAllRoleTypes
              required
              data-testid="role-picklist-field"
            />
          </FormFieldSection>
          <FormFieldSection
            title="Assign Users to this Binding"
            step={2}
            data-testid="assign-users-form-field-section"
          >
            <UserMultiSelect
              id="users"
              tooltip="Select users to assign this role"
              onChange={getParamsUpdater('users')}
              value={params.users}
              data-testid="users-multi-select"
            />
          </FormFieldSection>
          <FormFieldSection
            title="Assign Groups to this Binding"
            step={3}
            data-testid="assign-groups-form-field-section"
          >
            <GroupMultiSelect
              id="groups"
              tooltip="Select groups to assign this role"
              onChange={getParamsUpdater('groups')}
              value={params.groups}
              data-testid="groups-multi-select"
            />
          </FormFieldSection>
        </>
      </ModalForm>
    </>
  )
}
